import { Link, createFileRoute } from '@tanstack/react-router'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getListCustomersQueryKey,
  useApproveIdentityClaim,
  useCreateCustomer,
  useListCustomers,
  useListIdentityClaims,
  useRejectIdentityClaim,
} from '../../../api/generated/endpoints/customers/customers'
import type { CustomerRead, IdentityClaimRead } from '../../../api/generated/model'
import { apiMessage } from '../../../lib/api-error'
import { Field, INPUT } from './admin.tournaments'

/**
 * Everybody who has ever been here, and who can open the front door.
 *
 * The first screen in this app about a PERSON rather than a booking. Until it
 * existed a customer could only be found through the autocomplete on the
 * booking form — searchable, but never viewable, and invisible entirely if
 * they had not booked yet. Which is exactly what a member signed up for door
 * access is.
 */
export const Route = createFileRoute('/_authed/_admin/admin/customers')({
  component: AdminCustomersPage,
})

function AdminCustomersPage() {
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const customers = useListCustomers(
    { q: query, limit: 100 },
    { query: { placeholderData: keepPreviousData } },
  )
  const claims = useListIdentityClaims()
  const pending = claims.data?.claims ?? []

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-semibold">Clientes</h1>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Nuevo cliente
          </button>
        )}
      </div>

      {creating && <CreateForm onDone={() => setCreating(false)} />}

      {pending.length > 0 && <ClaimsPanel claims={pending} />}

      <label className="block">
        <span className="text-base font-medium text-slate-700">Buscar</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Nombre, teléfono o correo"
          className={`mt-1 ${INPUT}`}
        />
      </label>

      {customers.isPending && <p className="text-slate-600">Cargando…</p>}
      {customers.isError && (
        <p role="alert" className="text-red-700">
          No se pudieron cargar los clientes.
        </p>
      )}

      {customers.data && customers.data.customers.length === 0 && (
        <p className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">
          {query
            ? 'Ningún cliente coincide con esa búsqueda.'
            : 'Todavía no hay clientes registrados.'}
        </p>
      )}

      <ul className="space-y-3">
        {(customers.data?.customers ?? []).map((customer) => (
          <li key={customer.id}>
            <CustomerRow customer={customer} />
          </li>
        ))}
      </ul>

      {customers.data && customers.data.total > customers.data.customers.length && (
        <p className="text-base text-slate-600">
          Se muestran {customers.data.customers.length} de {customers.data.total}. Buscar por nombre
          para encontrar al resto.
        </p>
      )}
    </div>
  )
}

function CustomerRow({ customer }: { customer: CustomerRead }) {
  return (
    <Link
      to="/admin/customer/$userId"
      params={{ userId: customer.id }}
      className="flex min-h-20 flex-wrap items-center justify-between gap-3 rounded-lg border-2 border-slate-300 bg-white px-5 py-4 hover:bg-slate-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
    >
      <span className="flex flex-col">
        <span className="text-xl font-medium">{customer.name ?? 'Cliente sin nombre'}</span>
        <span className="text-base tabular-nums text-slate-700">
          {customer.phone ?? 'Sin teléfono registrado'}
        </span>
      </span>
      <span className="flex items-center gap-3">
        {customer.credit_hours > 0 && (
          <span className="rounded-full bg-slate-200 px-4 py-1 text-base text-slate-800">
            {customer.credit_hours} h
          </span>
        )}
        {customer.has_door_access && (
          <span className="rounded-full bg-green-100 px-4 py-1 text-base font-medium text-green-900">
            Con acceso
          </span>
        )}
      </span>
    </Link>
  )
}

/**
 * Signing somebody up at the desk.
 *
 * The correo field carries a hint out of all proportion to its size, because
 * it is the field that decides whether this person stays ONE customer. With it,
 * a later magic-link login attaches itself to this row automatically; without
 * it, the same human arriving online becomes a second row holding none of
 * their hours and none of their door access.
 */
function CreateForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateCustomer({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() })
        onDone()
      },
      onError: (err) => setError(apiMessage(err, 'No se pudo crear el cliente.')),
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        create.mutate({
          data: {
            name: name.trim(),
            phone: phone.trim() || null,
            email: email.trim() || null,
          },
        })
      }}
      className="space-y-5 rounded-lg border-2 border-slate-300 bg-white p-6"
    >
      <h2 className="text-2xl font-semibold">Nuevo cliente</h2>

      <Field label="Nombre">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={200}
          className={INPUT}
        />
      </Field>

      <Field label="Teléfono" hint="Formato +598…">
        <input
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="+59899123456"
          inputMode="tel"
          className={INPUT}
        />
      </Field>

      <Field
        label="Correo"
        hint="Conviene pedirlo: permite reconocer a la persona si después entra con su cuenta"
      >
        <input
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          inputMode="email"
          className={INPUT}
        />
      </Field>

      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={create.isPending}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {create.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-16 rounded-md border-2 border-slate-400 px-8 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Cancelar
        </button>
      </div>
    </form>
  )
}

/**
 * People who may already be in the system twice.
 *
 * Never automatic: a phone number is not proof of anything, and the row on the
 * other side of one of these may hold a key to the building.
 */
function ClaimsPanel({ claims }: { claims: IdentityClaimRead[] }) {
  return (
    <section className="space-y-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-6">
      <h2 className="text-2xl font-semibold text-amber-950">
        {claims.length === 1
          ? 'Un posible cliente repetido'
          : `${claims.length} posibles repetidos`}
      </h2>
      <p className="text-base text-amber-950">
        Alguien entró con su cuenta y coincide con una ficha creada en el mostrador. Al confirmar,
        las horas y el acceso quedan en una sola ficha.
      </p>
      <ul className="space-y-4">
        {claims.map((claim) => (
          <li key={claim.id}>
            <ClaimRow claim={claim} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function ClaimRow({ claim }: { claim: IdentityClaimRead }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const refresh = () => queryClient.invalidateQueries()

  const approve = useApproveIdentityClaim({
    mutation: {
      onSuccess: refresh,
      onError: (err) => setError(apiMessage(err, 'No se pudieron fusionar las fichas.')),
    },
  })
  const reject = useRejectIdentityClaim({
    mutation: {
      onSuccess: refresh,
      onError: (err) => setError(apiMessage(err, 'No se pudo descartar.')),
    },
  })

  const matched = claim.matched_on === 'email' ? 'mismo correo' : 'mismo teléfono'

  return (
    <div className="space-y-3 rounded-lg border-2 border-amber-200 bg-white p-5">
      <p className="text-lg">
        <span className="font-medium">{claim.claimant.name ?? 'Cliente sin nombre'}</span>
        <span className="text-slate-600"> entró con su cuenta ({matched})</span>
      </p>

      {claim.candidates.map((candidate) => (
        <div
          key={candidate.id}
          className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 px-4 py-3"
        >
          <span className="flex flex-col">
            <span className="text-lg">{candidate.name ?? 'Cliente sin nombre'}</span>
            <span className="text-base text-slate-700">
              {candidate.phone ?? 'Sin teléfono'}
              {candidate.credit_hours > 0 && ` · ${candidate.credit_hours} h`}
              {candidate.has_door_access && ' · con acceso'}
            </span>
          </span>
          <button
            type="button"
            disabled={approve.isPending}
            onClick={() => {
              setError(null)
              approve.mutate({ claimId: claim.id, data: { target_user_id: candidate.id } })
            }}
            className="min-h-14 rounded-md bg-slate-900 px-6 text-lg text-white disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Es la misma persona
          </button>
        </div>
      ))}

      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={reject.isPending}
        onClick={() => {
          setError(null)
          reject.mutate({ claimId: claim.id })
        }}
        className="min-h-14 rounded-md border-2 border-slate-400 px-6 text-lg disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        Son personas distintas
      </button>
    </div>
  )
}
